import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest } from "../../../Api/Api";
import { ENDPOINTS } from "../../../Api/Endpoints";
import { tokenManager } from "../../../Api/tokenManager";

const normalizeTenDigitMobile = (mobile) => {
  return String(mobile || "").replace(/\D/g, "").slice(-10);
};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.response?.data?.msg ||
  err?.message ||
  fallback;

const uploadFileToPresignedUrl = (uploadUrl, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", file?.type || "image/jpeg");

    xhr.upload.onprogress = (event) => {
      if (!event?.lengthComputable || typeof onProgress !== "function") return;
      const pct = Math.min(100, Math.max(0, Math.round((event.loaded / event.total) * 100)));
      onProgress(pct);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === "function") onProgress(100);
        resolve(true);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed due to network error"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(file);
  });

/* ================= PATIENT SEND OTP =================
 * Signup → POST /app/signup (create-or-update + OTP). Login → POST /app/login (existing user only).
 * Uses dedicated routes so behaviour does not depend on /app/send-otp defaults.
 */
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const mobile =
        typeof payload === "string" ? payload : String(payload?.mobile || "");
      const isLogin =
        typeof payload === "object" &&
        payload !== null &&
        String(payload?.intent || "").toLowerCase() === "login";

      const formattedMobile = normalizeTenDigitMobile(mobile);

      if (formattedMobile.length !== 10) {
        return rejectWithValue("Phone number must be exactly 10 digits");
      }

      const url = isLogin ? ENDPOINTS.PATIENT_LOGIN : ENDPOINTS.PATIENT_SIGNUP;

      const data = await apiRequest({
        method: "POST",
        url,
        data: { mobile: formattedMobile },
      });

      return {
        ...(data || {}),
        mobile: formattedMobile,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "OTP failed"));
    }
  }
);

/* ================= RESEND OTP ================= */
export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const mobile =
        typeof payload === "string" ? payload : String(payload?.mobile || "");
      const intent =
        typeof payload === "string"
          ? "login"
          : String(payload?.intent || "login").toLowerCase();

      const formattedMobile = normalizeTenDigitMobile(mobile);

      if (formattedMobile.length !== 10) {
        return rejectWithValue("Phone number must be exactly 10 digits");
      }

      const data = await apiRequest({
        method: "POST",
        url: ENDPOINTS.RESEND_OTP,
        data: {
          mobile: formattedMobile,
          intent: intent === "signup" ? "signup" : "login",
        },
      });

      return {
        ...(data || {}),
        mobile: formattedMobile,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Resend OTP failed"));
    }
  }
);

/* ================= VERIFY OTP ================= */
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ mobile, otp }, { rejectWithValue, getState }) => {
    try {
      const stateMobile = getState()?.auth?.otpSuccess?.mobile;
      const finalMobile = mobile || stateMobile;
      const formattedMobile = normalizeTenDigitMobile(finalMobile);
      const cleanOtp = String(otp || "").replace(/\D/g, "").slice(0, 4);

      if (formattedMobile.length !== 10) {
        return rejectWithValue("Phone number must be exactly 10 digits");
      }

      const data = await apiRequest({
        method: "POST",
        url: ENDPOINTS.VERIFY_OTP,
        data: {
          mobile: formattedMobile,
          otp: cleanOtp,
        },
      });

      const accessToken =
        data?.accessToken ||
        data?.token ||
        data?.data?.accessToken ||
        data?.data?.token ||
        null;

      const refreshToken =
        data?.refreshToken ||
        data?.data?.refreshToken ||
        null;

      if (accessToken) {
        tokenManager.setTokensForRole("patient", accessToken, refreshToken);
      }

      return {
        ...(data || {}),
        accessToken,
        refreshToken,
        mobile: formattedMobile,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "OTP verification failed"));
    }
  }
);

/* ================= COMPLETE PROFILE ================= */
/* Avatar: prefer S3 presigned upload (POST upload-url → PUT to S3 → complete-profile with avatarKey) so it works locally and on server. */
export const completeProfile = createAsyncThunk(
  "auth/completeProfile",
  async ({ name, gender, dob, bloodGroup, image, onAvatarUploadProgress, onAvatarUploadStatus }, { rejectWithValue }) => {
    try {
      let avatarKey = null;
      const trimmedDob = String(dob || "").trim();
      const trimmedBloodGroup = String(bloodGroup || "").trim();
      const baseProfileFields = {
        name: String(name || "").trim(),
        gender: String(gender || "").trim(),
      };
      if (trimmedDob) {
        baseProfileFields.dateOfBirth = trimmedDob;
        baseProfileFields.dob = trimmedDob;
      }
      if (trimmedBloodGroup) {
        baseProfileFields.bloodGroup = trimmedBloodGroup;
        baseProfileFields.blood_type = trimmedBloodGroup;
      }
      if (typeof onAvatarUploadProgress === "function") onAvatarUploadProgress(0);

      if (image && typeof image === "object" && image.size > 0) {
        const ext = (image.name && /\.(jpe?g|png|gif|webp)$/i.test(image.name))
          ? "." + image.name.split(".").pop().toLowerCase()
          : ".jpg";
        try {
          if (typeof onAvatarUploadStatus === "function") onAvatarUploadStatus("Requesting secure upload URL...");
          const uploadMeta = await apiRequest({
            method: "POST",
            url: ENDPOINTS.PROFILE_AVATAR_UPLOAD_URL,
            data: { ext },
          });
          const uploadUrl = uploadMeta?.uploadUrl || uploadMeta?.data?.uploadUrl;
          const key = uploadMeta?.key || uploadMeta?.data?.key;
          if (uploadUrl && key) {
            if (typeof onAvatarUploadStatus === "function") onAvatarUploadStatus("Uploading profile image...");
            await uploadFileToPresignedUrl(uploadUrl, image, onAvatarUploadProgress);
            avatarKey = key;
          }
        } catch (_) {
          /* fall back to FormData below */
        }
      }

      if (avatarKey) {
        if (typeof onAvatarUploadStatus === "function") onAvatarUploadStatus("Saving profile details...");
        const data = await apiRequest({
          method: "POST",
          url: ENDPOINTS.COMPLETE_PROFILE,
          data: { ...baseProfileFields, avatarKey },
        });
        return data;
      }

      const formData = new FormData();
      formData.append("name", baseProfileFields.name);
      formData.append("gender", baseProfileFields.gender);
      if (trimmedDob) {
        formData.append("dateOfBirth", trimmedDob);
        formData.append("dob", trimmedDob);
      }
      if (trimmedBloodGroup) {
        formData.append("bloodGroup", trimmedBloodGroup);
        formData.append("blood_type", trimmedBloodGroup);
      }
      if (image) formData.append("image", image);

      const data = await apiRequest({
        method: "POST",
        url: ENDPOINTS.COMPLETE_PROFILE,
        data: formData,
      });
      return data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Complete profile failed"));
    }
  }
);
